import { useDebounce } from '@sushiswap/hooks'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { quoteIntent } from 'src/lib/swap/trails'
import {
  Amount,
  Fraction,
  Percent,
  Price,
  ZERO,
  formatUSD,
  subtractSlippage,
} from 'sushi'
import { type EvmCurrency, EvmNative, isEvmWNativeSupported } from 'sushi/evm'
import { type Hex, stringify, zeroAddress } from 'viem'
import { useConnection } from 'wagmi'
import { usePrices } from '~evm/_common/ui/price-provider/price-provider/use-prices'
import { trailsAdapterToInternal, trailsNoRouteResponse } from './apiAdapter'
import type { UseEvmTradeParams, UseEvmTradeQuerySelect } from './types'
import { trailsQuoteValidator } from './validator02'

function getTokenAddress(token: EvmCurrency): string {
  return token.type === 'native' ? zeroAddress : token.wrap().address
}

export const useEvmTradeQuery = (
  {
    chainId,
    fromToken,
    toToken,
    amount,
    gasPrice = 50n,
    slippagePercentage,
    recipient,
    enabled,
  }: UseEvmTradeParams,
  select: UseEvmTradeQuerySelect,
) => {
  const { address } = useConnection()
  const debouncedAmount = useDebounce(amount, 500)

  return useQuery({
    queryKey: [
      'getTrade',
      {
        chainId,
        currencyA: fromToken,
        currencyB: toToken,
        amount: debouncedAmount,
        slippagePercentage,
        gasPrice,
        address,
        recipient,
      },
    ],
    queryFn: async () => {
      if (!address) throw new Error('No address')
      if (!chainId) throw new Error('No chainId')

      try {
        const res = await quoteIntent({
          ownerAddress: address,
          originChainId: chainId,
          originTokenAddress: getTokenAddress(fromToken as EvmCurrency),
          originTokenAmount: debouncedAmount?.amount.toString() ?? '0',
          destinationChainId: chainId,
          destinationTokenAddress: getTokenAddress(toToken as EvmCurrency),
          destinationToAddress: recipient ?? address,
          tradeType: 'EXACT_INPUT',
          options: {
            slippageTolerance: +slippagePercentage / 100,
          },
        })

        // Keep the raw intent before Zod strips unknown fields
        const rawIntent = res.intent

        const validated = trailsQuoteValidator.parse(res)
        const adapted = trailsAdapterToInternal(
          validated,
          fromToken as EvmCurrency,
          toToken as EvmCurrency,
          recipient,
        )

        // Override rawIntent with the unstripped version
        adapted.rawIntent = rawIntent

        // Set the wallet address as tx.from
        if (adapted.tx) {
          adapted.tx.from = address
        }

        return adapted
      } catch {
        return trailsNoRouteResponse(
          fromToken as EvmCurrency,
          toToken as EvmCurrency,
          recipient,
        )
      }
    },
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
    gcTime: 0,
    retry: false,
    select,
    enabled: Boolean(
      enabled &&
        address &&
        chainId &&
        fromToken &&
        toToken &&
        debouncedAmount &&
        gasPrice,
    ),
    queryKeyHashFn: stringify,
  })
}

export const useEvmTrade = (variables: UseEvmTradeParams) => {
  const {
    chainId,
    fromToken,
    toToken,
    amount,
    slippagePercentage,
    gasPrice,
    tokenTax,
  } = variables
  const { data: prices } = usePrices({
    chainId,
  })

  const nativePrice = useMemo(() => {
    if (
      prices &&
      chainId &&
      isEvmWNativeSupported(chainId) &&
      EvmNative.fromChainId(chainId).wrap().address !== zeroAddress
    ) {
      return prices.getFraction(EvmNative.fromChainId(chainId).wrap().address)
    }
    return new Fraction(0)
  }, [chainId, prices])

  const select: UseEvmTradeQuerySelect = useCallback(
    (data) => {
      if (
        chainId &&
        data &&
        amount &&
        data.route &&
        data.route.status !== 'NoWay' &&
        fromToken &&
        toToken
      ) {
        const amountIn = new Amount(fromToken, data.route.amountInBI)
        const amountOut = new Amount(
          toToken,
          new Fraction({ numerator: data.route.amountOutBI }).mul(
            tokenTax ? new Percent(1).sub(tokenTax) : 1,
          ).quotient,
        )

        // Use amountOutMin from API (via adapter) or compute from slippage
        const minAmountOut = data.args?.amountOutMin
          ? new Amount(toToken, data.args.amountOutMin)
          : subtractSlippage(
              new Amount(toToken, data.route.amountOutBI),
              new Percent({
                numerator: Math.floor(+slippagePercentage * 100),
                denominator: 10_000,
              }).toNumber(),
            )

        // Use API-provided price impact
        const priceImpact = data.route.priceImpact
          ? new Percent({
              numerator: Math.round(data.route.priceImpact * 100),
              denominator: 10000,
            })
          : new Percent(0)

        const gasSpent = gasPrice
          ? new Amount(EvmNative.fromChainId(chainId), gasPrice * 100_000n)
          : undefined

        return {
          swapPrice: amountOut.gt(ZERO)
            ? new Price({
                baseAmount: amount,
                quoteAmount: amountOut,
              })
            : undefined,
          priceImpact,
          amountIn,
          amountOut,
          minAmountOut,
          gasSpent: gasSpent?.toSignificant(4),
          gasSpentUsd:
            nativePrice && gasSpent
              ? gasSpent.mul(nativePrice.asFraction).toSignificant(4)
              : undefined,
          fee:
            'feeUsd' in data && typeof (data as any).feeUsd === 'number'
              ? formatUSD((data as any).feeUsd)
              : undefined,
          feePercent:
            'feePercent' in data ? (data as any).feePercent : undefined,
          route: data.route,
          status: data.route.status,
          tx: data?.tx
            ? {
                from: data.tx.from,
                to: data.tx.to,
                data: data.tx.data as Hex,
                value: data.tx.value,
                gas: data.tx.gas,
                gasPrice: data.tx.gasPrice,
              }
            : undefined,
          tokenTax,
          routingSource: 'Trails',
          intentId: 'intentId' in data ? (data as any).intentId : undefined,
          rawIntent: 'rawIntent' in data ? (data as any).rawIntent : undefined,
        }
      }

      return {
        swapPrice: undefined,
        priceImpact: undefined,
        amountIn: undefined,
        amountOut: undefined,
        minAmountOut: undefined,
        gasSpent: undefined,
        gasSpentUsd: undefined,
        fee: undefined,
        feePercent: undefined,
        route: undefined,
        status: undefined,
        tx: undefined,
        tokenTax: undefined,
        routingSource: undefined,
        intentId: undefined,
        rawIntent: undefined,
      }
    },
    [
      amount,
      chainId,
      fromToken,
      nativePrice,
      slippagePercentage,
      toToken,
      gasPrice,
      tokenTax,
    ],
  )

  return useEvmTradeQuery(variables, select)
}
